import { useState, useEffect } from 'react';

const CACHE_PREFIX = 'thumb3d_v1_';
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isObjUrl = (url) =>
  typeof url === 'string' && url?.toLowerCase()?.split('?')?.[0]?.endsWith('.obj');

const isGlbUrl = (url) => {
  if (typeof url !== 'string') return false;
  const path = url?.toLowerCase()?.split('?')?.[0];
  return path?.endsWith('.glb') || path?.endsWith('.gltf');
};

export const is3DModelUrl = (url) => isObjUrl(url) || isGlbUrl(url);

const getCacheKey = (url) => CACHE_PREFIX + btoa(url)?.replace(/[^a-zA-Z0-9]/g, '')?.slice(0, 60);

const readCache = (url) => {
  try {
    const key = getCacheKey(url);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { dataUrl, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return dataUrl;
  } catch {
    return null;
  }
};

const writeCache = (url, dataUrl) => {
  try {
    const key = getCacheKey(url);
    localStorage.setItem(key, JSON.stringify({ dataUrl, ts: Date.now() }));
  } catch {
    // Storage quota exceeded — silently ignore
  }
};

/**
 * Renders a 3D model (OBJ or GLB) into an offscreen canvas and returns a
 * data-URL thumbnail. Result is cached in localStorage for 7 days.
 *
 * @param {string|null} modelUrl  URL of the .obj / .glb / .gltf file
 * @returns {{ thumbnail: string|null, generating: boolean }}
 */
const use3DModelThumbnail = (modelUrl) => {
  const [thumbnail, setThumbnail] = useState(() => {
    if (!modelUrl || !is3DModelUrl(modelUrl)) return null;
    return readCache(modelUrl);
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!modelUrl || !is3DModelUrl(modelUrl)) return;

    // Already cached
    const cached = readCache(modelUrl);
    if (cached) {
      setThumbnail(cached);
      return;
    }

    let cancelled = false;
    setGenerating(true);

    const generate = async () => {
      try {
        const THREE = await import('three');

        // ── Offscreen canvas ──────────────────────────────────────────────
        const W = 480;
        const H = 320;
        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false });
        renderer?.setSize(W, H, false);
        renderer?.setPixelRatio(1);
        renderer.outputColorSpace = THREE?.SRGBColorSpace;
        renderer.toneMapping = THREE?.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1a1a2e);

        // Lighting
        scene?.add(new THREE.AmbientLight(0xffffff, 1.0));
        const dir1 = new THREE.DirectionalLight(0xffffff, 1.2);
        dir1?.position?.set(5, 10, 7);
        scene?.add(dir1);
        const dir2 = new THREE.DirectionalLight(0xffffff, 0.5);
        dir2?.position?.set(-5, -5, -5);
        scene?.add(dir2);

        const camera = new THREE.PerspectiveCamera(45, W / H, 0.01, 1000);

        let object;

        if (isGlbUrl(modelUrl)) {
          const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
          const loader = new GLTFLoader();
          const gltf = await new Promise((resolve, reject) =>
            loader.load(modelUrl, resolve, undefined, reject)
          );
          object = gltf?.scene;
          object?.traverse((child) => {
            if (child?.isMesh && child?.material) {
              const mats = Array.isArray(child?.material) ? child?.material : [child?.material];
              mats?.forEach((m) => { if (m) m.side = THREE?.DoubleSide; });
            }
          });
        } else {
          const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
          const loader = new OBJLoader();
          // Try loading MTL for colours
          try {
            const { MTLLoader } = await import('three/examples/jsm/loaders/MTLLoader.js');
            const mtlUrl = modelUrl?.replace(/\.obj(\?.*)?$/i, (_, qs) => `.mtl${qs || ''}`);
            const mtlLoader = new MTLLoader();
            const materials = await new Promise((resolve, reject) =>
              mtlLoader.load(mtlUrl, resolve, undefined, reject)
            );
            materials?.preload();
            loader?.setMaterials(materials);
          } catch {
            // MTL not available — use default material
          }
          object = await new Promise((resolve, reject) =>
            loader.load(modelUrl, resolve, undefined, reject)
          );
          object?.traverse((child) => {
            if (child?.isMesh) {
              if (!child?.material) {
                child.material = new THREE.MeshStandardMaterial({
                  color: 0xffffff,
                  vertexColors: !!child.geometry?.attributes?.color,
                  side: THREE.DoubleSide,
                });
              } else {
                const mats = Array.isArray(child?.material) ? child?.material : [child?.material];
                mats?.forEach((m) => {
                  if (m) {
                    m.side = THREE?.DoubleSide;
                    if (child?.geometry?.attributes?.color) m.vertexColors = true;
                  }
                });
              }
            }
          });
        }

        if (cancelled) { renderer?.dispose(); return; }

        // Centre & scale
        const box = new THREE.Box3()?.setFromObject(object);
        const center = box?.getCenter(new THREE.Vector3());
        const size = box?.getSize(new THREE.Vector3());
        const maxDim = Math.max(size?.x, size?.y, size?.z) || 1;
        const scale = 3 / maxDim;
        object?.scale?.setScalar(scale);
        object?.position?.sub(center?.multiplyScalar(scale));
        scene?.add(object);

        // Slightly elevated camera angle for a nice preview
        const dist = maxDim * scale * 2.5;
        camera?.position?.set(dist * 0.6, dist * 0.4, dist);
        camera?.lookAt(0, 0, 0);

        renderer?.render(scene, camera);

        const dataUrl = canvas?.toDataURL('image/jpeg', 0.82);

        renderer?.dispose();

        if (!cancelled) {
          writeCache(modelUrl, dataUrl);
          setThumbnail(dataUrl);
        }
      } catch (err) {
        // Generation failed — thumbnail stays null, card falls back to static image
        console.warn('[3D Thumbnail] generation failed:', err?.message);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    };

    generate();
    return () => { cancelled = true; };
  }, [modelUrl]);

  return { thumbnail, generating };
};

export default use3DModelThumbnail;
